import React from 'react';

const PrintableInvoice = React.forwardRef(({ rental, paperSize = '80mm' }, ref) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleString('id-ID', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Use final_price if available (for completed rentals), otherwise total_price
  const totalAmount = rental.final_price || rental.total_price || 0;
  
  // Calculate F&B total from additional items
  const fbTotal = rental.additional_items?.reduce((sum, item) => sum + (item.price * item.qty), 0) || 0;
  
  // Base rental price = total - F&B
  const baseRentalPrice = totalAmount - fbTotal;

  // Paper size configurations for thermal printer
  const paperSizeConfig = {
    '58mm': { width: '58mm', maxWidth: '220px', fontSize: 'text-xs', padding: 'p-2' },
    '80mm': { width: '80mm', maxWidth: '300px', fontSize: 'text-sm', padding: 'p-4' },
    'custom': { width: '100%', maxWidth: '400px', fontSize: 'text-base', padding: 'p-6' }
  };

  const config = paperSizeConfig[paperSize] || paperSizeConfig['80mm'];

  return (
    <div ref={ref} className="print-invoice bg-white text-black mx-auto" style={{ width: config.width, maxWidth: config.maxWidth }}>
      <style>{`
        @media print {
          @page {
            size: ${config.width} auto;
            margin: 0mm;
          }
          body {
            margin: 0;
            padding: 0;
          }
          body * {
            visibility: hidden;
          }
          .print-invoice, .print-invoice * {
            visibility: visible;
          }
          .print-invoice {
            position: absolute;
            left: 0;
            top: 0;
            width: ${config.width};
            box-shadow: none;
          }
          .no-print {
            display: none !important;
          }
        }
      `}</style>

      <div className={config.padding}>

        {/* Header - Thermal Optimized */}
        <div className="text-center border-b-2 border-black pb-2 mb-3">
          <h1 className={`font-black ${paperSize === '58mm' ? 'text-lg' : 'text-2xl'} mb-1`}>
            RIMURU GAME SUITE
          </h1>
          <p className="text-xs">Rental PlayStation</p>
          <p className="text-xs mt-1">================================</p>
        </div>

        {/* Invoice Info */}
        <div className="mb-3 text-xs border-b border-dashed border-gray-400 pb-2">
          <div className="flex justify-between mb-1">
            <span className="font-bold">No Invoice:</span>
            <span>{rental.id?.substring(0, 8).toUpperCase()}</span>
          </div>
          <div className="flex justify-between">
            <span className="font-bold">Tanggal:</span>
            <span>{formatDateTime(rental.actual_end_time || rental.stopped_at || rental.end_time)}</span>
          </div>
        </div>

        {/* Customer Info */}
        <div className="mb-3 text-xs border-b border-dashed border-gray-400 pb-2">
          <p className="font-bold mb-1">CUSTOMER:</p>
          <p className="font-semibold">{rental.customer_name}</p>
          <p>{rental.customer_phone || '-'}</p>
        </div>

        {/* Rental Details */}
        <div className="mb-3 text-xs border-b border-dashed border-gray-400 pb-2">
          <p className="font-bold mb-2">DETAIL RENTAL:</p>
          
          <div className="space-y-1">
            <div className="flex justify-between">
              <span>Unit PS:</span>
              <span className="font-bold">{rental.ps_unit_name}</span>
            </div>
            
            <div className="flex justify-between">
              <span>Mode:</span>
              <span className="font-bold">
                {rental.rental_mode === 'paket' ? 'PAKET' :
                 rental.rental_mode === 'no_limit' ? 'NO LIMIT' :
                 'DURASI'}
              </span>
            </div>

            {rental.package_name && (
              <div className="flex justify-between">
                <span>Paket:</span>
                <span className="font-bold">{rental.package_name}</span>
              </div>
            )}

            <div className="flex justify-between">
              <span>Durasi:</span>
              <span className="font-semibold">
                {rental.actual_duration_hours 
                  ? `${Math.round(rental.actual_duration_hours * 60)} menit`
                  : rental.duration_minutes 
                    ? `${rental.duration_minutes} menit`
                    : '-'
                }
              </span>
            </div>

            <div className="flex justify-between">
              <span>Cabang:</span>
              <span>{rental.branch || 'Cabang Utama'}</span>
            </div>
          </div>
        </div>

        {/* Pricing */}
        <div className="mb-3 text-xs">
          <div className="space-y-1">
            <div className="flex justify-between">
              <span>Biaya Rental:</span>
              <span className="font-semibold">{formatCurrency(baseRentalPrice)}</span>
            </div>

            {rental.additional_items && rental.additional_items.length > 0 && (
              <>
                <div className="border-t border-dashed border-gray-400 pt-1 mt-1">
                  <p className="font-bold mb-1">ITEM F&B:</p>
                </div>
                {rental.additional_items.map((item, idx) => (
                  <div key={idx} className="flex justify-between pl-2">
                    <span>{item.name} x{item.qty}</span>
                    <span>{formatCurrency(item.price * item.qty)}</span>
                  </div>
                ))}
              </>
            )}

            <div className="border-t-2 border-black pt-2 mt-2">
              <div className="flex justify-between font-bold text-base">
                <span>TOTAL:</span>
                <span>{formatCurrency(totalAmount)}</span>
              </div>
            </div>

            <div className="flex justify-between mt-1">
              <span>Status:</span>
              <span className="font-bold">LUNAS</span>
            </div>

            {rental.payment_method && (
              <div className="flex justify-between">
                <span>Metode Bayar:</span>
                <span className="font-semibold uppercase">{rental.payment_method}</span>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="text-center text-xs border-t-2 border-black pt-2 mt-3">
          <p className="mb-1">Terima kasih atas kunjungan Anda!</p>
          <p className="font-bold">Rimuru Game Suite</p>
          <p className="mt-1">Your Gaming Paradise </p>
          <p className="mt-2 text-gray-500 text-[10px]">Nota dicetak otomatis oleh sistem</p>
        </div>
      </div>
    </div>
  );
});

PrintableInvoice.displayName = 'PrintableInvoice';

export default PrintableInvoice;
